import { useEffect, useSyncExternalStore } from "react";
import {
  getPrivateSessionState,
  subscribePrivateSession,
  establishPrivateSessionBinding,
} from "@/lib/api/private-session";
import { acceptInspectedSellerSession } from "./sessionChannel";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/core/query-keys";
import { readSession, takeSessionCredentialRefresh } from "./readSession";

type UseSessionQueryParams = {
  storeSlug: string;
};

export function useSessionQuery({ storeSlug }: UseSessionQueryParams) {
  const queryClient = useQueryClient();
  const bindingInspection = storeSlug === "password-change";
  const session = useSyncExternalStore(subscribePrivateSession, getPrivateSessionState);
  const query = useQuery({
    queryKey: queryKeys.seller.session(bindingInspection ? storeSlug : session.storeSlug ?? storeSlug),
    queryFn: async ({ queryKey, signal }) => {
      const generation = getPrivateSessionState().generation;
      const payload = await readSession(queryClient, queryKey, signal);
      await acceptInspectedSellerSession(queryClient, payload, generation);
      if (generation === getPrivateSessionState().generation) establishPrivateSessionBinding(generation, payload.seller.storeSlug, payload.seller.id);
      return payload;
    },
    enabled: storeSlug.length > 0 && !session.changing,
    refetchOnWindowFocus: "always",
    // Access revisions and focus re-inspect capabilities without an idle timer.
  });
  const { data, refetch } = query;
  useEffect(() => {
    if (!session.changing && data && takeSessionCredentialRefresh(queryClient)) void refetch();
  }, [data, queryClient, refetch, session.changing]);

  // If the query has no successful data yet (e.g. a stale 401 error from a
  // previous session) and is currently re-fetching (e.g. right after login),
  // treat it as loading so ProtectedLayout doesn't redirect to /login before
  // the fresh response arrives.
  const hasSuccessfulData = !!query.data;
  // A fresh inspected mismatch must remain visible to the route redirect;
  // an old cached payload must stay hidden while the binding is repaired.
  const hasWrongStore = !bindingInspection && session.storeSlug &&
    session.storeSlug !== storeSlug && query.data?.seller.storeSlug !== session.storeSlug;
  const isSettling = session.changing || Boolean(hasWrongStore) ||
    query.isLoading || (!hasSuccessfulData && query.isFetching);

  return {
    data: isSettling ? undefined : query.data,
    isLoading: isSettling,
    error: isSettling ? null : query.error,
    refetch: query.refetch,
  };
}
